import { useEffect, useMemo, useRef, useState } from 'react';
import * as PIXI from 'pixi.js';
import {
  BadgeAlert,
  Cpu,
  Handshake,
  Landmark,
  Lock,
  Orbit,
  Package,
  Radio,
  Rocket,
  Shield,
  Sparkles,
  Sun,
  Swords,
  Zap,
} from 'lucide-react';
import {
  useCreateFleet,
  useCreateSpacePort,
  useOrderFleet,
  usePostSpaceTradeOffer,
  useResearchSpaceTechnology,
  useSpaceConquestMap,
  useUpgradeSpacePort,
} from '../../hooks/useSpaceConquest';
import { useCivilisation } from '../../hooks/useCivilisation';
import type { Fleet, FleetType, SpacePort, SpaceTechnology } from '../../api/spaceConquest';
import { CreatureAvatar } from '../creatures/CreaturePanel';
import ToastContainer from '../toast/ToastContainer';

interface SpaceGameViewProps {
  onOpenPlanet: () => void;
}

const PANEL_TABS = [
  { id: 'civilisation', label: 'Civilisation', icon: Landmark },
  { id: 'technology', label: 'Technology', icon: Cpu },
  { id: 'fleets', label: 'Fleets', icon: Rocket },
  { id: 'communications', label: 'Comms', icon: Radio },
] as const;

// Mirrors backend base fleet costs (without tech bonuses) so pupils can see the price before building.
const FLEET_TYPES: Array<{ id: FleetType; label: string; icon: typeof Rocket; credits: number; energy: number }> = [
  { id: 'exploration', label: 'Exploration', icon: Sparkles, credits: 166, energy: 85 },
  { id: 'trade', label: 'Cargo', icon: Handshake, credits: 273, energy: 105 },
  { id: 'war', label: 'Battle', icon: BadgeAlert, credits: 281, energy: 145 },
  { id: 'defence', label: 'Defence', icon: Shield, credits: 243, energy: 125 },
];

// Mirrors calculateSpaceStructureCost on the backend.
const STRUCTURE_COSTS = {
  port: (count: number) => ({ credits: 180 + count * 40, energy: 80 + count * 25 }),
  harvester: (count: number) => ({ credits: 90 + count * 30, energy: 40 + count * 15 }),
};

const HARVEST_RADIUS = 12;

const SPACE_ASSETS = {
  background: '/assets/space/space_map_background.png',
  majorStar: '/assets/space/star_major_harvest.png',
  homeStar: '/assets/space/star_home_marker.png',
  harvestEffect: '/assets/space/effect_energy_harvest.png',
  shieldEffect: '/assets/space/effect_shield.png',
  ports: [
    '/assets/space/spaceport_level_1.png',
    '/assets/space/spaceport_level_2.png',
    '/assets/space/spaceport_level_3.png',
  ],
  fleets: {
    exploration: '/assets/space/fleet_exploration.png',
    trade: '/assets/space/fleet_trade.png',
    war: '/assets/space/fleet_war.png',
    defence: '/assets/space/fleet_defence.png',
  } satisfies Record<FleetType, string>,
};

function shortGroup(label: string) {
  return label.replace(/\s+(I|II|III)$/, '').replace(/-/g, ' ');
}

function groupTechs(technologies: SpaceTechnology[]) {
  const groups = new Map<string, SpaceTechnology[]>();
  for (const tech of technologies) {
    const list = groups.get(tech.tech_group) || [];
    list.push(tech);
    groups.set(tech.tech_group, list);
  }
  return [...groups.values()].map((items) => items.sort((a, b) => a.tier - b.tier));
}

function ownPorts(ports: SpacePort[], planetId?: string) {
  return ports.filter((port) => port.planet_id === planetId && port.status === 'active');
}

export default function SpaceGameView({ onOpenPlanet }: SpaceGameViewProps) {
  const { data, isLoading, isError, error } = useSpaceConquestMap();
  const civilisationQuery = useCivilisation(Boolean(data?.spaceUnlocked));
  const containerRef = useRef<HTMLDivElement>(null);
  const [selectedCell, setSelectedCell] = useState<{ x: number; y: number } | null>(null);
  const [selectedFleetId, setSelectedFleetId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<(typeof PANEL_TABS)[number]['id']>('civilisation');
  const [cargoCredits, setCargoCredits] = useState(0);
  const [cargoEnergy, setCargoEnergy] = useState(0);

  const createPort = useCreateSpacePort();
  const upgradePort = useUpgradeSpacePort();
  const createFleet = useCreateFleet();
  const orderFleet = useOrderFleet();
  const researchTech = useResearchSpaceTechnology();
  const tradeOffer = usePostSpaceTradeOffer();

  const visibleCells = useMemo(() => new Set(data?.fog.visibleCells || []), [data?.fog.visibleCells]);
  const selectedFleet = data?.fleets.find((fleet) => fleet.fleet_id === selectedFleetId) || null;
  const ports = useMemo(() => ownPorts(data?.ports || [], data?.planetId), [data?.ports, data?.planetId]);
  const ownRealPorts = useMemo(() => ports.filter((port) => (port.port_type || 'port') === 'port'), [ports]);
  const ownHarvesters = useMemo(() => ports.filter((port) => port.port_type === 'harvester'), [ports]);
  const homePort = ownRealPorts.find((port) => port.is_home) || ownRealPorts[0];
  const selectedPort = data?.ports.find((port) => selectedCell && port.grid_x === selectedCell.x && port.grid_y === selectedCell.y) || null;
  const ownSelectedPort = selectedPort?.planet_id === data?.planetId ? selectedPort : null;
  const enemySelectedPort = selectedPort && selectedPort.planet_id !== data?.planetId ? selectedPort : null;
  const spaceAmbassador = civilisationQuery.data?.data?.civilisation?.ambassador;
  const portCost = STRUCTURE_COSTS.port(ownRealPorts.length);
  const harvesterCost = STRUCTURE_COSTS.harvester(ownHarvesters.length);

  // Pirate or derelict on the selected cell, if scouted and still active.
  const selectedNpc = useMemo(() => {
    if (!selectedCell || !data) return null;
    return data.stars.find((star) =>
      (star.star_type === 'pirate' || star.star_type === 'derelict')
      && star.visible !== false
      && ((star.properties as Record<string, any>)?.status === 'active')
      && star.grid_x === selectedCell.x
      && star.grid_y === selectedCell.y) || null;
  }, [selectedCell, data]);

  // How good is the selected cell as a harvest spot? Based on the nearest visible major star.
  const harvestPreview = useMemo(() => {
    if (!selectedCell || !data) return null;
    let best: { dist: number; energyOutput: number } | null = null;
    for (const star of data.stars) {
      if (!star.is_major || star.visible === false) continue;
      const dist = Math.hypot(star.grid_x - selectedCell.x, star.grid_y - selectedCell.y);
      if (!best || dist < best.dist) best = { dist, energyOutput: star.energy_output };
    }
    if (!best) return null;
    const quality = best.dist <= 3 ? 'Excellent' : best.dist <= 6 ? 'Good' : best.dist <= HARVEST_RADIUS ? 'Weak' : 'Out of range';
    return { ...best, inRange: best.dist <= HARVEST_RADIUS, quality };
  }, [selectedCell, data]);
  useEffect(() => {
    if (!containerRef.current || !data) return;
    containerRef.current.querySelectorAll('canvas').forEach((canvas) => canvas.remove());

    let app: PIXI.Application | null = null;
    let frame = 0;

    const init = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      if (rect.width < 20 || rect.height < 20) {
        frame = requestAnimationFrame(init);
        return;
      }

      app = new PIXI.Application({
        backgroundColor: 0x020617,
        resizeTo: containerRef.current,
        antialias: true,
        resolution: window.devicePixelRatio || 1,
        autoDensity: true,
      });
      const canvas = app.view as HTMLCanvasElement;
      canvas.style.width = '100%';
      canvas.style.height = '100%';
      containerRef.current.appendChild(canvas);

      const root = new PIXI.Container();
      app.stage.addChild(root);
      const pad = 34;
      const gridSize = data.grid.size;
      const scale = Math.min((app.screen.width - pad * 2) / gridSize, (app.screen.height - pad * 2) / gridSize);
      const offsetX = (app.screen.width - gridSize * scale) / 2;
      const offsetY = (app.screen.height - gridSize * scale) / 2;
      const toScreen = (x: number, y: number) => ({ x: offsetX + x * scale, y: offsetY + y * scale });
      const addSprite = (path: string, x: number, y: number, size: number, alpha = 1, blendMode?: PIXI.BLEND_MODES) => {
        const sprite = new PIXI.Sprite(PIXI.Texture.from(path));
        sprite.anchor.set(0.5);
        sprite.x = x;
        sprite.y = y;
        sprite.width = size;
        sprite.height = size;
        sprite.alpha = alpha;
        if (blendMode !== undefined) sprite.blendMode = blendMode;
        root.addChild(sprite);
        return sprite;
      };

      const background = new PIXI.TilingSprite(PIXI.Texture.from(SPACE_ASSETS.background), app.screen.width, app.screen.height);
      background.alpha = 0.62;
      root.addChild(background);

      const bg = new PIXI.Graphics();
      for (let i = 0; i < 160; i++) {
        const x = (i * 97) % Math.max(1, app.screen.width);
        const y = (i * 53) % Math.max(1, app.screen.height);
        bg.beginFill(0xdbeafe, i % 7 === 0 ? 0.28 : 0.12);
        bg.drawCircle(x, y, i % 9 === 0 ? 1.2 : 0.7);
        bg.endFill();
      }
      root.addChild(bg);

      const grid = new PIXI.Graphics();
      grid.lineStyle(1, 0x8fbce6, 0.12);
      for (let i = 0; i <= gridSize; i++) {
        const x = offsetX + i * scale;
        const y = offsetY + i * scale;
        grid.moveTo(x, offsetY);
        grid.lineTo(x, offsetY + gridSize * scale);
        grid.moveTo(offsetX, y);
        grid.lineTo(offsetX + gridSize * scale, y);
      }
      root.addChild(grid);

      const fog = new PIXI.Graphics();
      fog.beginFill(0x020617, 0.68);
      for (let x = 0; x < gridSize; x++) {
        for (let y = 0; y < gridSize; y++) {
          if (!visibleCells.has(`${x}:${y}`)) {
            fog.drawRect(offsetX + x * scale, offsetY + y * scale, scale + 0.2, scale + 0.2);
          }
        }
      }
      fog.endFill();
      root.addChild(fog);

      const routes = new PIXI.Graphics();
      data.fleets.forEach((fleet) => {
        if (fleet.destination_x == null || fleet.destination_y == null) return;
        const start = toScreen(Number(fleet.grid_x), Number(fleet.grid_y));
        const end = toScreen(fleet.destination_x, fleet.destination_y);
        routes.lineStyle(2, fleet.planet_id === data.planetId ? 0x22d3ee : 0xf97316, 0.45);
        routes.moveTo(start.x, start.y);
        routes.lineTo(end.x, end.y);
      });
      root.addChild(routes);

      data.stars.forEach((star) => {
        if (star.visible === false) return;
        const point = toScreen(star.grid_x, star.grid_y);
        const radius = star.is_major ? Math.max(5, scale * 0.45) : Math.max(2, scale * 0.18);
        if (star.star_type === 'pirate' || star.star_type === 'derelict') {
          const props = (star.properties as Record<string, any>) || {};
          if (props.status !== 'active') return;
          const isPirate = star.star_type === 'pirate';
          // NPCs reuse the fleet art: red hostile war ship for pirates, a grey
          // listing cargo hull with a salvage glow for derelicts.
          const glow = addSprite(
            isPirate ? SPACE_ASSETS.shieldEffect : SPACE_ASSETS.harvestEffect,
            point.x,
            point.y,
            Math.max(24, scale * (isPirate ? 3.4 : 3.0)),
            isPirate ? 0.4 : 0.3,
            PIXI.BLEND_MODES.ADD,
          );
          glow.tint = isPirate ? 0xef4444 : 0x67e8f9;
          const body = addSprite(
            isPirate ? SPACE_ASSETS.fleets.war : SPACE_ASSETS.fleets.trade,
            point.x,
            point.y,
            Math.max(20, scale * (isPirate ? 3.0 : 2.6)),
            isPirate ? 0.95 : 0.8,
          );
          body.tint = isPirate ? 0xf87171 : 0x94a3b8;
          body.rotation = isPirate ? Math.PI * 0.75 : 0.5;
          body.eventMode = 'static';
          body.cursor = 'pointer';
          body.on('pointerdown', (event) => {
            event.stopPropagation();
            setSelectedCell({ x: star.grid_x, y: star.grid_y });
            setSelectedFleetId(null);
          });
          return;
        }
        if (star.is_major) {
          addSprite(SPACE_ASSETS.harvestEffect, point.x, point.y, radius * 7.2, 0.35, PIXI.BLEND_MODES.ADD);
          addSprite(SPACE_ASSETS.majorStar, point.x, point.y, radius * 5.2, 0.95, PIXI.BLEND_MODES.ADD);
        } else if (star.is_home_slot) {
          addSprite(SPACE_ASSETS.homeStar, point.x, point.y, Math.max(20, radius * 6.5), 0.95, PIXI.BLEND_MODES.ADD);
        } else {
          const body = new PIXI.Graphics();
          body.beginFill(0xffffff, 0.86);
          body.drawCircle(point.x, point.y, radius);
          body.endFill();
          root.addChild(body);
        }
      });

      data.ports.forEach((port) => {
        const point = toScreen(port.grid_x, port.grid_y);
        const own = port.planet_id === data.planetId;
        const isHarvester = port.port_type === 'harvester';
        const spritePath = isHarvester
          ? SPACE_ASSETS.ports[0]
          : SPACE_ASSETS.ports[Math.max(0, Math.min(SPACE_ASSETS.ports.length - 1, port.level - 1))];
        const portSize = Math.max(24, scale * (port.is_home ? 3.2 : isHarvester ? 2.2 : 2.8));
        if (isHarvester) {
          addSprite(SPACE_ASSETS.harvestEffect, point.x, point.y, portSize * 1.6, own ? 0.4 : 0.25, PIXI.BLEND_MODES.ADD);
        } else if (port.is_home || port.level > 1) {
          addSprite(SPACE_ASSETS.shieldEffect, point.x, point.y, portSize * 1.35, own ? 0.34 : 0.22, PIXI.BLEND_MODES.ADD);
        }
        const body = addSprite(spritePath, point.x, point.y, portSize, own ? 0.98 : 0.76);
        if (isHarvester) body.tint = 0xfbbf24;
        body.eventMode = 'static';
        body.cursor = 'pointer';
        body.on('pointerdown', (event) => {
          event.stopPropagation();
          setSelectedCell({ x: port.grid_x, y: port.grid_y });
          setSelectedFleetId(null);
        });
        root.addChild(body);
      });

      data.fleets.forEach((fleet) => {
        if (fleet.status === 'destroyed') return;
        const point = toScreen(Number(fleet.grid_x), Number(fleet.grid_y));
        const own = fleet.planet_id === data.planetId;
        const body = addSprite(SPACE_ASSETS.fleets[fleet.fleet_type], point.x, point.y, Math.max(22, scale * 3.1), own ? 0.96 : 0.72);
        body.rotation = own ? 0 : Math.PI;
        body.eventMode = 'static';
        body.cursor = 'pointer';
        body.on('pointerdown', (event) => {
          event.stopPropagation();
          setSelectedFleetId(fleet.fleet_id);
          setSelectedCell({ x: Math.round(Number(fleet.grid_x)), y: Math.round(Number(fleet.grid_y)) });
          setActiveTab('fleets');
        });
        root.addChild(body);
      });

      if (selectedCell) {
        const point = toScreen(selectedCell.x, selectedCell.y);
        const marker = new PIXI.Graphics();
        marker.lineStyle(2, 0xffffff, 0.9);
        marker.drawRect(point.x - scale / 2, point.y - scale / 2, scale, scale);
        root.addChild(marker);
      }

      app.stage.eventMode = 'static';
      app.stage.hitArea = app.screen;
      app.stage.on('pointerdown', (event) => {
        const local = event.global;
        const x = Math.floor((local.x - offsetX) / scale);
        const y = Math.floor((local.y - offsetY) / scale);
        if (x >= 0 && y >= 0 && x < gridSize && y < gridSize) {
          setSelectedCell({ x, y });
          setSelectedFleetId(null);
        }
      });
    };

    frame = requestAnimationFrame(init);
    return () => {
      cancelAnimationFrame(frame);
      app?.destroy(true, { children: true });
    };
  }, [data, selectedCell, visibleCells]);

  if (isLoading) {
    return <div className="flex h-[100dvh] items-center justify-center bg-slate-950 text-cyan-300">Loading star map...</div>;
  }

  if (isError || !data) {
    return (
      <div className="flex h-[100dvh] items-center justify-center bg-slate-950 text-red-300">
        {(error as any)?.response?.data?.error?.message || 'Space map unavailable'}
      </div>
    );
  }

  const firstOwnFleet = data.fleets.find((fleet) => fleet.planet_id === data.planetId);
  const selectedOwnFleet = selectedFleet?.planet_id === data.planetId ? selectedFleet : firstOwnFleet || null;

  const buildStructure = (structureType: 'port' | 'harvester') => {
    if (!selectedCell) return;
    createPort.mutate({ gridX: selectedCell.x, gridY: selectedCell.y, structureType });
  };

  const orderSelectedFleet = (orderType: 'move' | 'patrol' = 'move') => {
    if (!selectedCell || !selectedOwnFleet) return;
    orderFleet.mutate({ fleetId: selectedOwnFleet.fleet_id, input: { orderType, gridX: selectedCell.x, gridY: selectedCell.y } });
  };

  const attackSelectedPort = () => {
    if (!enemySelectedPort || !selectedOwnFleet) return;
    orderFleet.mutate({ fleetId: selectedOwnFleet.fleet_id, input: { orderType: 'attack', targetPortId: enemySelectedPort.space_port_id } });
  };

  const interceptAt = () => {
    if (!selectedCell || !selectedOwnFleet) return;
    orderFleet.mutate({ fleetId: selectedOwnFleet.fleet_id, input: { orderType: 'intercept', gridX: selectedCell.x, gridY: selectedCell.y } });
  };

  const deliverCargo = () => {
    if (!selectedPort || !selectedOwnFleet || (cargoCredits <= 0 && cargoEnergy <= 0)) return;
    orderFleet.mutate({
      fleetId: selectedOwnFleet.fleet_id,
      input: { orderType: 'trade', targetPortId: selectedPort.space_port_id, cargoCredits, cargoEnergy },
    });
    setCargoCredits(0);
    setCargoEnergy(0);
  };

  const fleetIdle = selectedOwnFleet ? ['idle', 'completed'].includes(selectedOwnFleet.status) : false;

  return (
    <div className="flex h-[100dvh] flex-col overflow-hidden bg-slate-950 text-slate-100">
      <header className="flex shrink-0 items-center justify-between gap-3 border-b border-slate-800 bg-slate-950/95 px-3 py-2 md:px-5">
        <div className="flex min-w-0 items-center gap-3">
          <Orbit className="h-5 w-5 text-cyan-300" />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-white">PlanetSeed Star Map</div>
            <div className="text-[11px] text-slate-400">Day {data.currentDay} · Play day {data.activePlayDay}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="flex items-center gap-1 rounded-md border border-yellow-500/25 bg-yellow-950/30 px-2 py-1 text-yellow-200">
            <Zap className="h-3.5 w-3.5" /> {data.resources.energy}
          </span>
          <span className="rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-slate-200">{data.resources.credits}cr</span>
          <button onClick={onOpenPlanet} className="rounded-md border border-slate-700 bg-slate-900 px-2.5 py-1.5 text-slate-200 hover:border-cyan-500/60">
            Planet
          </button>
        </div>
      </header>

      <div className="flex min-h-0 flex-1">
        <aside className="hidden w-64 shrink-0 border-r border-slate-800 bg-slate-950/90 p-3 md:block">
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div className="rounded-md border border-slate-800 bg-slate-900 p-2">
              <div className="text-slate-500">Ports</div>
              <div className="text-lg font-semibold text-cyan-200">{ownRealPorts.length}</div>
            </div>
            <div className="rounded-md border border-slate-800 bg-slate-900 p-2">
              <div className="text-slate-500">Harvesters</div>
              <div className="text-lg font-semibold text-yellow-200">{ownHarvesters.length}</div>
            </div>
            <div className="rounded-md border border-slate-800 bg-slate-900 p-2">
              <div className="text-slate-500">Fleets</div>
              <div className="text-lg font-semibold text-emerald-200">{data.fleets.filter((f) => f.planet_id === data.planetId && f.status !== 'destroyed').length}</div>
            </div>
          </div>

          <div className="mt-3 rounded-md border border-slate-800 bg-slate-900 p-3">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Selected</div>
            <div className="text-sm text-white">{selectedCell ? `${selectedCell.x}:${selectedCell.y}` : 'None'}</div>
            {selectedPort && (
              <div className="mt-2 text-xs text-slate-300">
                <div>{selectedPort.name}{enemySelectedPort ? <span className="ml-1 text-orange-300">(rival)</span> : null}</div>
                <div className="text-slate-500">Level {selectedPort.level} · {selectedPort.port_type === 'harvester' ? 'Harvester' : selectedPort.is_home ? 'Home' : selectedPort.status}</div>
              </div>
            )}
            {selectedNpc && (() => {
              const props = (selectedNpc.properties as Record<string, any>) || {};
              const isPirate = selectedNpc.star_type === 'pirate';
              const pirateStrength = Number(props.strength?.defence || 0) + Number(props.strength?.shields || 0);
              const fleetPower = selectedOwnFleet ? selectedOwnFleet.attack + selectedOwnFleet.shields : 0;
              return (
                <div className={`mt-2 rounded-md border px-2 py-1.5 text-[11px] ${isPirate ? 'border-red-800/60 bg-red-950/30 text-red-200' : 'border-cyan-800/60 bg-cyan-950/30 text-cyan-200'}`}>
                  {isPirate ? (
                    <>
                      <div className="font-semibold">Pirate fleet · strength {pirateStrength}</div>
                      <div className="mt-0.5">Bounty: {props.bountyCredits || 0}cr + {props.bountyEnergy || 0}e</div>
                      {selectedOwnFleet && (
                        <div className={`mt-0.5 ${fleetPower > pirateStrength ? 'text-emerald-300' : 'text-orange-300'}`}>
                          {selectedOwnFleet.name} power {fleetPower} — {fleetPower > pirateStrength ? 'would win this fight' : 'too weak, it would be destroyed'}
                        </div>
                      )}
                      <div className="mt-0.5 text-red-300/80">Move a battle fleet here to claim the bounty. Pirates raid nearby harvesters!</div>
                    </>
                  ) : (
                    <>
                      <div className="font-semibold">Derelict ship</div>
                      <div className="mt-0.5">Salvage: {props.salvageCredits || 0}cr + {props.salvageEnergy || 0}e</div>
                      <div className="mt-0.5 text-cyan-300/80">Move any fleet here to recover the salvage.</div>
                    </>
                  )}
                </div>
              );
            })()}
            {selectedCell && harvestPreview && !selectedNpc && (
              <div className={`mt-2 flex items-center gap-1.5 text-[11px] ${harvestPreview.inRange ? 'text-yellow-200' : 'text-slate-500'}`}>
                <Sun className="h-3.5 w-3.5" />
                {harvestPreview.inRange
                  ? `${harvestPreview.quality} harvest spot · star ${harvestPreview.dist.toFixed(1)} cells away (${harvestPreview.energyOutput}e/day pool)`
                  : 'No star in harvest range — structures here collect nothing'}
              </div>
            )}
            <div className="mt-3 grid gap-2">
              <button
                disabled={!selectedCell || createPort.isPending || !!selectedPort}
                onClick={() => buildStructure('port')}
                className="rounded-md bg-cyan-700 px-3 py-2 text-xs font-medium text-white hover:bg-cyan-600 disabled:bg-slate-800 disabled:text-slate-500"
              >
                Build Spaceport · {portCost.credits}cr {portCost.energy}e
              </button>
              <button
                disabled={!selectedCell || createPort.isPending || !!selectedPort}
                onClick={() => buildStructure('harvester')}
                className="rounded-md bg-yellow-700 px-3 py-2 text-xs font-medium text-white hover:bg-yellow-600 disabled:bg-slate-800 disabled:text-slate-500"
              >
                Build Harvester · {harvesterCost.credits}cr {harvesterCost.energy}e
              </button>
              <button
                disabled={!ownSelectedPort || upgradePort.isPending}
                onClick={() => ownSelectedPort && upgradePort.mutate(ownSelectedPort.space_port_id)}
                className="rounded-md bg-slate-800 px-3 py-2 text-xs font-medium text-slate-100 hover:bg-slate-700 disabled:text-slate-500"
              >
                Upgrade Structure
              </button>
              <button
                disabled={!selectedCell || !selectedOwnFleet || !fleetIdle || orderFleet.isPending}
                onClick={() => orderSelectedFleet('move')}
                className="rounded-md bg-emerald-700 px-3 py-2 text-xs font-medium text-white hover:bg-emerald-600 disabled:bg-slate-800 disabled:text-slate-500"
              >
                Move Fleet
              </button>
              {data.settings.conflictEnabled && enemySelectedPort && !enemySelectedPort.is_home && (
                <button
                  disabled={!selectedOwnFleet || !fleetIdle || orderFleet.isPending}
                  onClick={attackSelectedPort}
                  className="flex items-center justify-center gap-1.5 rounded-md bg-red-800 px-3 py-2 text-xs font-medium text-white hover:bg-red-700 disabled:bg-slate-800 disabled:text-slate-500"
                >
                  <Swords className="h-3.5 w-3.5" /> Attack {selectedPort?.name}
                </button>
              )}
              {data.settings.conflictEnabled && selectedFleet && selectedFleet.planet_id !== data.planetId && (
                <button
                  disabled={!selectedOwnFleet || !fleetIdle || orderFleet.isPending}
                  onClick={interceptAt}
                  className="flex items-center justify-center gap-1.5 rounded-md bg-orange-800 px-3 py-2 text-xs font-medium text-white hover:bg-orange-700 disabled:bg-slate-800 disabled:text-slate-500"
                >
                  <Swords className="h-3.5 w-3.5" /> Intercept Fleet
                </button>
              )}
            </div>
          </div>

          <div className="mt-3 rounded-md border border-slate-800 bg-slate-900 p-3">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Space Events</div>
            <div className="space-y-2">
              {data.events.slice(0, 5).map((event) => (
                <div key={event.space_event_id} className="border-b border-slate-800 pb-2 last:border-0 last:pb-0">
                  <div className="text-xs font-medium text-slate-100">{event.title}</div>
                  <div className="text-[11px] text-slate-500">Day {event.created_day}</div>
                </div>
              ))}
            </div>
          </div>
        </aside>

        <main className="relative min-w-0 flex-1">
          <div ref={containerRef} className="absolute inset-0" />
          <div className="absolute bottom-[49dvh] left-3 right-3 rounded-md border border-slate-800 bg-slate-950/85 px-3 py-2 text-xs text-slate-300 backdrop-blur md:bottom-3 md:right-auto">
            <div>
              {selectedCell ? `Grid ${selectedCell.x}:${selectedCell.y}` : 'No grid selected'}
              {selectedFleet && <span className="ml-2 text-emerald-300">{selectedFleet.name}</span>}
              {selectedNpc && (
                <span className={`ml-2 ${selectedNpc.star_type === 'pirate' ? 'text-red-300' : 'text-cyan-300'}`}>
                  {selectedNpc.star_type === 'pirate'
                    ? `Pirates! Bounty ${(selectedNpc.properties as any)?.bountyCredits || 0}cr`
                    : `Derelict · salvage ${(selectedNpc.properties as any)?.salvageCredits || 0}cr`}
                </span>
              )}
              {harvestPreview?.inRange && selectedCell && !selectedNpc && (
                <span className="ml-2 text-yellow-200">{harvestPreview.quality} harvest spot</span>
              )}
            </div>
            <div className="mt-2 flex gap-1.5 md:hidden">
              <button
                disabled={!selectedCell || createPort.isPending || !!selectedPort}
                onClick={() => buildStructure('port')}
                className="flex-1 rounded bg-cyan-700 px-2 py-1.5 text-[11px] font-medium text-white disabled:bg-slate-800 disabled:text-slate-500"
              >
                Port {portCost.credits}cr
              </button>
              <button
                disabled={!selectedCell || createPort.isPending || !!selectedPort}
                onClick={() => buildStructure('harvester')}
                className="flex-1 rounded bg-yellow-700 px-2 py-1.5 text-[11px] font-medium text-white disabled:bg-slate-800 disabled:text-slate-500"
              >
                Harvester {harvesterCost.credits}cr
              </button>
              <button
                disabled={!selectedCell || !selectedOwnFleet || !fleetIdle || orderFleet.isPending}
                onClick={() => orderSelectedFleet('move')}
                className="flex-1 rounded bg-emerald-700 px-2 py-1.5 text-[11px] font-medium text-white disabled:bg-slate-800 disabled:text-slate-500"
              >
                Move
              </button>
              {data.settings.conflictEnabled && enemySelectedPort && !enemySelectedPort.is_home && (
                <button
                  disabled={!selectedOwnFleet || !fleetIdle || orderFleet.isPending}
                  onClick={attackSelectedPort}
                  className="flex-1 rounded bg-red-800 px-2 py-1.5 text-[11px] font-medium text-white disabled:bg-slate-800 disabled:text-slate-500"
                >
                  Attack
                </button>
              )}
            </div>
          </div>
        </main>

        <aside className="fixed inset-x-0 bottom-0 z-20 flex max-h-[48dvh] min-h-0 flex-col border-t border-slate-800 bg-slate-950/95 md:static md:z-auto md:max-h-none md:w-[min(420px,42vw)] md:min-w-80 md:shrink-0 md:border-l md:border-t-0">
          <nav className="grid grid-cols-4 border-b border-slate-800">
            {PANEL_TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex min-h-12 flex-col items-center justify-center gap-1 text-[10px] ${activeTab === tab.id ? 'bg-slate-900 text-cyan-200' : 'text-slate-500 hover:bg-slate-900/70'}`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>

          <div className="min-h-0 flex-1 overflow-y-auto p-3">
            {activeTab === 'civilisation' && (
              <div className="space-y-3">
                {spaceAmbassador && (
                  <section className="rounded-md border border-cyan-800/50 bg-slate-900 p-3">
                    <div className="flex items-center gap-3">
                      <CreatureAvatar type={spaceAmbassador.baseSpecies} variant="spaceAmbassador" className="h-16 w-16 rounded-md bg-slate-950" />
                      <div className="min-w-0">
                        <h2 className="truncate text-sm font-semibold text-white">{spaceAmbassador.evolvedTitle}</h2>
                        <div className="mt-1 text-xs text-cyan-200">{spaceAmbassador.trait} space-age envoy</div>
                        <div className="mt-1 text-[11px] text-slate-500">Civilisation command has moved to orbital operations.</div>
                      </div>
                    </div>
                  </section>
                )}
                <section className="rounded-md border border-slate-800 bg-slate-900 p-3">
                  <h2 className="text-sm font-semibold text-white">Star Supremacy</h2>
                  <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                    <div><span className="text-slate-500">Trade</span><div className={data.settings.tradeEnabled ? 'text-emerald-300' : 'text-slate-500'}>{data.settings.tradeEnabled ? 'Enabled' : 'Disabled'}</div></div>
                    <div><span className="text-slate-500">Conflict</span><div className={data.settings.conflictEnabled ? 'text-orange-300' : 'text-slate-500'}>{data.settings.conflictEnabled ? 'Enabled' : 'Disabled'}</div></div>
                    <div><span className="text-slate-500">Home Zone</span><div className="text-cyan-200">{data.settings.homeSafeZoneRadius} cells</div></div>
                    <div><span className="text-slate-500">Season</span><div className="text-cyan-200">{data.settings.seasonEndDay ? `Ends day ${data.settings.seasonEndDay}` : 'Teacher controlled'}</div></div>
                  </div>
                </section>
                <section className="rounded-md border border-slate-800 bg-slate-900 p-3">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400">Energy Influence</h3>
                  <div className="mt-2 space-y-1.5 text-xs">
                    {data.energyInfluence.filter((item) => item.planetId === data.planetId).slice(0, 8).map((item) => (
                      <div key={`${item.starId}-${item.portId}`} className="flex justify-between gap-2">
                        <span className="truncate text-slate-400">{data.ports.find((port) => port.space_port_id === item.portId)?.name || 'Port'}</span>
                        <span className="font-mono text-yellow-200">+{item.energyPerDay}/d</span>
                      </div>
                    ))}
                  </div>
                </section>
              </div>
            )}

            {activeTab === 'technology' && (
              <div className="space-y-2">
                {data.technologies.some((tech) => tech.status === 'researching') && (
                  <div className="rounded-md border border-cyan-800/50 bg-cyan-950/30 px-3 py-2 text-[11px] text-cyan-200">
                    Research in progress — one technology can be researched at a time.
                  </div>
                )}
                {groupTechs(data.technologies).map((group) => {
                  const first = group[0];
                  return (
                    <section key={first.tech_group} className="rounded-md border border-slate-800 bg-slate-900 p-3">
                      <div className="mb-2 flex items-center justify-between">
                        <h3 className="text-sm font-semibold capitalize text-white">{shortGroup(first.name)}</h3>
                        <span className="text-[10px] text-slate-500">{group.filter((t) => t.status === 'completed').length}/3 researched</span>
                      </div>
                      <div className="grid gap-1.5">
                        {group.map((tech) => (
                          <button
                            key={tech.space_technology_definition_id}
                            disabled={tech.locked || tech.status !== 'available' || researchTech.isPending}
                            onClick={() => researchTech.mutate(tech.space_technology_definition_id)}
                            className={`flex min-h-10 items-center justify-between gap-2 rounded-md border px-2 py-1.5 text-left text-xs ${
                              tech.status === 'completed'
                                ? 'border-emerald-800/50 bg-emerald-950/20'
                                : tech.status === 'researching'
                                ? 'border-cyan-700/60 bg-cyan-950/20'
                                : tech.locked
                                ? 'border-slate-800 bg-slate-950 opacity-50'
                                : 'border-slate-800 bg-slate-950 hover:border-cyan-600'
                            }`}
                            style={{ touchAction: 'manipulation' }}
                          >
                            <span className="flex min-w-0 items-center gap-1.5">
                              {tech.locked && tech.status === 'available' && <Lock className="h-3 w-3 shrink-0 text-slate-500" />}
                              <span className={`truncate ${tech.status === 'completed' ? 'text-emerald-300' : 'text-slate-200'}`}>{tech.name}</span>
                            </span>
                            <span className="shrink-0 text-[11px]">
                              {tech.status === 'completed' ? (
                                <span className="text-emerald-400">Done ✓</span>
                              ) : tech.status === 'researching' ? (
                                <span className="animate-pulse text-cyan-300">Researching...</span>
                              ) : tech.locked ? (
                                <span className="text-slate-600">Needs previous tier</span>
                              ) : (
                                <span className="text-slate-500">{tech.credit_cost}cr · {tech.energy_cost}e · {tech.research_days}d</span>
                              )}
                            </span>
                          </button>
                        ))}
                      </div>
                    </section>
                  );
                })}
              </div>
            )}

            {activeTab === 'fleets' && (
              <div className="space-y-3">
                <section className="rounded-md border border-slate-800 bg-slate-900 p-3">
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Fleet Builder</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {FLEET_TYPES.map((item) => {
                      const Icon = item.icon;
                      return (
                        <button
                          key={item.id}
                          disabled={!homePort || createFleet.isPending}
                          onClick={() => createFleet.mutate({ fleetType: item.id, originPortId: homePort?.space_port_id })}
                          className="flex flex-col items-start gap-1 rounded-md border border-slate-800 bg-slate-950 px-2 py-2 text-xs hover:border-cyan-600 disabled:opacity-45"
                        >
                          <span className="flex items-center gap-2"><Icon className="h-4 w-4 text-cyan-300" />{item.label}</span>
                          <span className="text-[10px] text-slate-500">from {item.credits}cr · {item.energy}e</span>
                        </button>
                      );
                    })}
                  </div>
                  {!homePort && <p className="mt-2 text-[11px] text-amber-400">A spaceport is required to build fleets.</p>}
                </section>

                {selectedOwnFleet && selectedPort && fleetIdle && (
                  <section className="rounded-md border border-amber-800/50 bg-slate-900 p-3">
                    <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-300">
                      <Package className="h-3.5 w-3.5" /> Cargo Run to {selectedPort.name}
                    </h3>
                    <p className="text-[11px] text-slate-500">
                      Load up to {selectedOwnFleet.cargo_capacity} cargo onto {selectedOwnFleet.name}. Resources are spent when loading and delivered on arrival.
                    </p>
                    <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                      <label className="flex flex-col gap-1 text-slate-400">
                        Credits
                        <input
                          type="number"
                          min={0}
                          max={selectedOwnFleet.cargo_capacity}
                          value={cargoCredits}
                          onChange={(event) => setCargoCredits(Math.max(0, Math.min(selectedOwnFleet.cargo_capacity, Math.floor(Number(event.target.value) || 0))))}
                          className="rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-slate-100"
                        />
                      </label>
                      <label className="flex flex-col gap-1 text-slate-400">
                        Energy
                        <input
                          type="number"
                          min={0}
                          max={Math.max(0, selectedOwnFleet.cargo_capacity - cargoCredits)}
                          value={cargoEnergy}
                          onChange={(event) => setCargoEnergy(Math.max(0, Math.min(selectedOwnFleet.cargo_capacity - cargoCredits, Math.floor(Number(event.target.value) || 0))))}
                          className="rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-slate-100"
                        />
                      </label>
                    </div>
                    <button
                      disabled={(cargoCredits <= 0 && cargoEnergy <= 0) || orderFleet.isPending}
                      onClick={deliverCargo}
                      className="mt-2 w-full rounded-md bg-amber-700 px-3 py-2 text-xs font-medium text-white hover:bg-amber-600 disabled:bg-slate-800 disabled:text-slate-500"
                    >
                      Send Cargo
                    </button>
                  </section>
                )}

                <section className="rounded-md border border-slate-800 bg-slate-900 p-3">
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Active Fleets</h3>
                  <div className="space-y-1.5">
                    {data.fleets.filter((fleet) => fleet.planet_id === data.planetId && fleet.status !== 'destroyed').map((fleet: Fleet) => (
                      <button
                        key={fleet.fleet_id}
                        onClick={() => setSelectedFleetId(fleet.fleet_id)}
                        className={`w-full rounded-md border px-2 py-2 text-left text-xs ${selectedFleetId === fleet.fleet_id ? 'border-emerald-500 bg-emerald-950/30' : 'border-slate-800 bg-slate-950'}`}
                      >
                        <div className="flex justify-between">
                          <span className="font-medium text-slate-100">{fleet.name}</span>
                          <span className="text-slate-500">{fleet.status}</span>
                        </div>
                        <div className="mt-1 text-[11px] text-slate-500">
                          A{fleet.attack} D{fleet.defence} S{fleet.shields} · {Math.round(Number(fleet.grid_x))}:{Math.round(Number(fleet.grid_y))}
                          {fleet.arrival_day ? ` · arrives day ${fleet.arrival_day}` : ''}
                          {(fleet.cargo_credits > 0 || fleet.cargo_energy > 0)
                            ? ` · carrying ${[fleet.cargo_credits > 0 ? `${fleet.cargo_credits}cr` : '', fleet.cargo_energy > 0 ? `${fleet.cargo_energy}e` : ''].filter(Boolean).join(' ')}`
                            : ''}
                        </div>
                      </button>
                    ))}
                  </div>
                </section>
              </div>
            )}

            {activeTab === 'communications' && (
              <div className="space-y-3">
                <section className="rounded-md border border-slate-800 bg-slate-900 p-3">
                  <h3 className="text-sm font-semibold text-white">Preset Communications</h3>
                  <div className="mt-2 grid gap-2 text-xs">
                    {['Open Trade Channel', 'Request Peace', 'Issue Warning', 'Offer Energy Exchange'].map((label) => (
                      <button key={label} className="rounded-md border border-slate-800 bg-slate-950 px-3 py-2 text-left text-slate-300 hover:border-cyan-600">
                        {label}
                      </button>
                    ))}
                  </div>
                </section>
                <section className="rounded-md border border-slate-800 bg-slate-900 p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400">Trade Offers</h3>
                    <button
                      onClick={() => tradeOffer.mutate({ offeredResource: 'energy', offeredAmount: 50, requestedResource: 'credits', requestedAmount: 75 })}
                      className="rounded-md bg-amber-700 px-2 py-1 text-[10px] text-white hover:bg-amber-600"
                    >
                      Post 50e
                    </button>
                  </div>
                  <div className="space-y-1.5">
                    {data.tradeOffers.slice(0, 8).map((offer) => (
                      <button
                        key={offer.space_trade_offer_id}
                        disabled={offer.seller_planet_id === data.planetId || tradeOffer.isPending}
                        onClick={() => tradeOffer.mutate({ offerId: offer.space_trade_offer_id })}
                        className="flex w-full items-center justify-between rounded-md border border-slate-800 bg-slate-950 px-2 py-1.5 text-left text-xs text-slate-300 hover:border-amber-600 disabled:opacity-45"
                      >
                        <span>{offer.offered_amount} {offer.offered_resource}</span>
                        <span className="text-slate-500">for {offer.requested_amount} {offer.requested_resource}</span>
                      </button>
                    ))}
                  </div>
                </section>
              </div>
            )}
          </div>
        </aside>
      </div>
      <ToastContainer />
    </div>
  );
}
